import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const RILicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: [
        '24 Learning Units per 2-year cycle',
        'AIA-CES accredited providers accepted',
        'HSW (Health, Safety & Welfare) emphasis recommended',
        'License renews biennially',
      ],
      stateBoard: 'Rhode Island Board of Examination and Registration of Architects',
      boardUrl: 'https://dbr.ri.gov/divisions/comercial-licensing/professions/architects',
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        '30 PDH per 2-year renewal period',
        'Max 15 PDH from self-directed study',
        'NSPE and NCEES guidelines followed',
        'License renews biennially on June 30',
      ],
      stateBoard: 'Rhode Island Board of Registration for Professional Engineers',
      boardUrl: 'https://dbr.ri.gov/divisions/comercial-licensing/professions/engineers',
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: [
        '80 CPE hours per 2-year reporting period',
        '4 hours Ethics required',
        'Minimum 20 hours per year',
        'NASBA Registry approved sponsors',
      ],
      stateBoard: 'Rhode Island Board of Accountancy',
      boardUrl: 'https://dbr.ri.gov/divisions/comercial-licensing/professions/accountants',
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 10,
      cycleYears: 1,
      terminology: 'CLE credits',
      highlights: [
        '10 CLE credits per year',
        '2 hours Ethics and Professional Responsibility',
        'RI Supreme Court MCLE Program rules apply',
        'Live and on-demand CLE accepted',
      ],
      stateBoard: 'Rhode Island Supreme Court — MCLE Commission',
      boardUrl: 'https://www.courts.ri.gov/Courts/SupremeCourt/Pages/MCLE.aspx',
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 10,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '10 CE contact hours per 2-year renewal period',
        'Approved by ANA or national nursing accreditors',
        'License renews biennially on January 31',
        'CE must relate to practice area',
      ],
      stateBoard: 'Rhode Island Board of Nurse Registration and Nursing Education',
      boardUrl: 'https://health.ri.gov/licenses/detail.php?id=231',
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE hours',
      highlights: [
        '30 CE hours per 2-year cycle',
        '3 hours Law required',
        'ACPE-accredited providers required',
        'License renews biennially on September 30',
      ],
      stateBoard: 'Rhode Island Board of Pharmacy',
      boardUrl: 'https://health.ri.gov/licenses/detail.php?id=228',
    },
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Rhode Island Professional License Renewal Requirements',
      description:
        'Complete guide to Rhode Island professional license renewal CE requirements for architects, engineers, CPAs, attorneys, nurses, and pharmacists.',
      url: 'https://cectrack.com/ri-license-renewal',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cectrack.com' },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Rhode Island License Renewal',
            item: 'https://cectrack.com/ri-license-renewal',
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How many CE hours do I need in Rhode Island?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Rhode Island CE requirements vary by profession. Architects need 24 LU biennially, PEs need 30 PDH biennially, CPAs need 80 CPE hours every 2 years (including 4 ethics hours), attorneys need 10 CLE credits annually (including 2 ethics hours), RNs need 10 CE hours biennially, and pharmacists need 30 CE hours biennially.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which agency handles professional licenses in Rhode Island?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Rhode Island Department of Business Regulation (DBR) oversees most professional licenses including architects, engineers, and CPAs. The RI Department of Health licenses nurses and pharmacists. The RI Supreme Court MCLE Commission oversees attorney CLE requirements.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use online courses for Rhode Island CE requirements?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, online and on-demand CE courses from accredited providers are generally accepted in Rhode Island. Attorneys can fulfill CLE requirements with live or recorded programs approved by the MCLE Commission.',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Rhode Island Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Rhode Island professional license renewal CE requirements for architects, engineers, CPAs, attorneys, nurses, and more. Track all your RI CE credits in one place."
        keywords="Rhode Island license renewal, RI CE requirements, Rhode Island continuing education, Rhode Island professional licenses, RI architects, RI engineers, RI CPAs"
        canonical="https://cectrack.com/ri-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            opacity: 0.4,
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1
                style={{
                  fontSize: '3rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                Rhode Island Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track your continuing education requirements across all your Rhode Island
                professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to="/register"
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  Start Tracking Free
                </Link>
                <a
                  href="#professions"
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main-content">
        {/* Professions Grid */}
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>
                Rhode Island CE Requirements by Profession
              </h2>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'Mandated Training'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                          Key Requirements:
                        </div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => (
                            <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>
                          ))}
                        </ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                          Visit State Board →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why CEC Track */}
        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                  Simplify Your Rhode Island License Management
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                  CEC Track automatically tracks requirements, calculates progress, and sends reminders
                  so you never miss a Rhode Island renewal deadline.
                </p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {['Automated deadline tracking', 'Upload certificates to requirements', 'Real-time progress by category', 'Multi-state support', 'Secure cloud storage', 'Email notifications'].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Join professionals across Rhode Island who trust CEC Track.</p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>
                    Create Free Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* State Resources */}
        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Rhode Island State Resources</h2>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>RI Department of Business Regulation</h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Licenses architects, engineers, CPAs, and other business and professional occupations in Rhode Island.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> (401) 462-9500</div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://dbr.ri.gov" target="_blank" rel="noopener noreferrer">dbr.ri.gov</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Address:</strong> 1511 Pontiac Ave, Cranston, RI 02920</div>
                    <a href="https://dbr.ri.gov/divisions/comercial-licensing" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">Commercial Licensing</a>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h4 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>RI Department of Health — Licensing</h4>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Licenses and regulates nurses, pharmacists, and other healthcare professionals in Rhode Island.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> (401) 222-5960</div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://health.ri.gov" target="_blank" rel="noopener noreferrer">health.ri.gov</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Address:</strong> 3 Capitol Hill, Providence, RI 02908</div>
                    <a href="https://health.ri.gov/licenses/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">Health Licenses</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="RI" />

        {/* CTA */}
        <section aria-label="Call to action" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}>
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Ready to Simplify Your Rhode Island License Renewal?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>Join professionals across Rhode Island who never worry about missing CE requirements</p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Get Started Free</Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default RILicenseRenewal;
